import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Users, Search, Shield, ShieldCheck, UserCog, Trash2, 
  Mail, Building, Calendar, Edit, Clock, Check, X, UserPlus, Ban, Unlock, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration des applications disponibles
const AVAILABLE_APPS = [
  { key: 'dashboard', label: 'Tableau de bord', category: 'Principal' },
  { key: 'organisation', label: 'Organisation (toutes)', category: 'Organisation' },
  { key: 'planning', label: 'Planification', category: 'Organisation' },
  { key: 'time-tracking', label: 'Suivi du temps', category: 'Organisation' },
  { key: 'forms', label: 'Listes de contrôle', category: 'Organisation' },
  { key: 'tasks', label: 'Gestion des Tâches', category: 'Organisation' },
  { key: 'communication', label: 'Communication (toutes)', category: 'Communication' },
  { key: 'messages', label: 'Messages', category: 'Communication' },
  { key: 'chat', label: 'Chat', category: 'Communication' },
  { key: 'news', label: 'Actualités', category: 'Communication' },
  { key: 'directory', label: 'Annuaire', category: 'Communication' },
  { key: 'knowledge', label: 'Méthodes', category: 'Communication' },
  { key: 'support', label: 'Assistance technique', category: 'Communication' },
  { key: 'events', label: 'Événements', category: 'Communication' },
  { key: 'hr', label: 'Espace RH (toutes)', category: 'Espace RH' },
  { key: 'training', label: 'Formation', category: 'Espace RH' },
  { key: 'documents', label: 'Documents', category: 'Espace RH' },
  { key: 'recognition', label: 'Reconnaissance', category: 'Espace RH' },
  { key: 'leaves', label: 'Congés', category: 'Espace RH' },
  { key: 'notifications', label: 'Notifications', category: 'Système' },
];

export default function UsersPage() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToBlock, setUserToBlock] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [processingUser, setProcessingUser] = useState(null);
  const [appsDialogOpen, setAppsDialogOpen] = useState(false);
  const [selectedUserForApps, setSelectedUserForApps] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);
  const [savingApps, setSavingApps] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filter out pending users
        setUsers(data.filter(u => u.status !== 'pending'));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users/pending/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingUsers(data);
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (res.ok) {
        setUsers(users.map(u => 
          u.id === userId ? { ...u, role: newRole } : u
        ));
        toast.success('Rôle mis à jour avec succès');
        setEditDialogOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du rôle');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const res = await fetch(`${API_URL}/api/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setUsers(users.filter(u => u.id !== userToDelete.id));
        setPendingUsers(pendingUsers.filter(u => u.id !== userToDelete.id));
        toast.success('Utilisateur supprimé avec succès');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleBlockUser = async () => {
    if (!userToBlock) return;

    const isBlocked = userToBlock.status === 'blocked';
    const endpoint = isBlocked ? 'unblock' : 'block';

    try {
      const res = await fetch(`${API_URL}/api/users/${userToBlock.id}/${endpoint}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const newStatus = isBlocked ? 'active' : 'blocked';
        setUsers(users.map(u => 
          u.id === userToBlock.id ? { ...u, status: newStatus } : u
        ));
        toast.success(isBlocked ? 'Utilisateur débloqué' : 'Utilisateur bloqué - Une notification lui a été envoyée');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'opération');
    } finally {
      setBlockDialogOpen(false);
      setUserToBlock(null);
    }
  };

  const handleActivateUser = async (userId, action) => {
    setProcessingUser(userId);
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/activate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action })
      });

      if (res.ok) {
        const data = await res.json();
        
        if (action === 'approve') {
          toast.success(data.message + (data.email_sent ? ' Email envoyé !' : ''));
          // Move user from pending to active
          const approvedUser = pendingUsers.find(u => u.id === userId);
          if (approvedUser) {
            setPendingUsers(pendingUsers.filter(u => u.id !== userId));
            setUsers([...users, { ...approvedUser, status: 'active' }]);
          }
        } else {
          toast.success(data.message);
          setPendingUsers(pendingUsers.filter(u => u.id !== userId));
        }
        
        fetchUsers();
        fetchPendingUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors du traitement');
      }
    } catch (error) {
      toast.error('Erreur lors du traitement de la demande');
    } finally {
      setProcessingUser(null);
    }
  };

  const openAppsDialog = async (user) => {
    setSelectedUserForApps(user);
    try {
      const res = await fetch(`${API_URL}/api/users/${user.id}/visible-apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.is_admin) {
          toast.info('Les administrateurs ont accès à toutes les applications');
          return;
        }
        setSelectedApps(data.visible_apps || []);
        setAppsDialogOpen(true);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des permissions');
    }
  };

  const handleAppToggle = (appKey) => {
    setSelectedApps(prev => {
      if (prev.includes(appKey)) {
        return prev.filter(a => a !== appKey);
      } else {
        return [...prev, appKey];
      }
    });
  };

  const handleSaveApps = async () => {
    if (!selectedUserForApps) return;
    setSavingApps(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${selectedUserForApps.id}/visible-apps`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ visible_apps: selectedApps })
      });

      if (res.ok) {
        toast.success('Permissions mises à jour avec succès');
        setAppsDialogOpen(false);
        fetchUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des permissions');
    } finally {
      setSavingApps(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
          <ShieldCheck className="w-3 h-3 mr-1" />
          Administrateur
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        <Shield className="w-3 h-3 mr-1" />
        Employé
      </Badge>
    );
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const adminCount = users.filter(u => u.role === 'admin').length;
  const employeeCount = users.filter(u => u.role === 'employee').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <UserCog className="w-7 h-7" />
          Gestion des Utilisateurs
        </h1>
        <p className="text-muted-foreground mt-1">
          Gérez les comptes utilisateurs et les demandes d'inscription
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <Users className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-muted-foreground">Utilisateurs actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <ShieldCheck className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{adminCount}</p>
                <p className="text-sm text-muted-foreground">Administrateurs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{employeeCount}</p>
                <p className="text-sm text-muted-foreground">Employés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={pendingUsers.length > 0 ? 'border-orange-200 bg-orange-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${pendingUsers.length > 0 ? 'bg-orange-200' : 'bg-slate-100'}`}>
                <Clock className={`w-6 h-6 ${pendingUsers.length > 0 ? 'text-orange-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingUsers.length}</p>
                <p className="text-sm text-muted-foreground">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Active Users and Pending Requests */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" className="gap-2">
            <Users className="w-4 h-4" />
            Utilisateurs actifs ({users.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <UserPlus className="w-4 h-4" />
            Demandes en attente
            {pendingUsers.length > 0 && (
              <Badge className="ml-1 bg-orange-500 text-white">{pendingUsers.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Active Users Tab */}
        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle>Utilisateurs actifs</CardTitle>
              <CardDescription>
                {filteredUsers.length} utilisateur(s) trouvé(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher par nom ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-users"
                  />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-role">
                    <SelectValue placeholder="Filtrer par rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les rôles</SelectItem>
                    <SelectItem value="admin">Administrateurs</SelectItem>
                    <SelectItem value="employee">Employés</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Users Table */}
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead className="hidden md:table-cell">Email</TableHead>
                      <TableHead className="hidden lg:table-cell">Département</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead className="hidden md:table-cell">Inscrit le</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Aucun utilisateur trouvé
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9">
                                <AvatarImage src={user.avatar} />
                                <AvatarFallback className="bg-slate-100">
                                  {getInitials(user.first_name, user.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">
                                  {user.first_name} {user.last_name}
                                </p>
                                <p className="text-xs text-muted-foreground md:hidden">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="w-4 h-4" />
                              {user.email}
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {user.department ? (
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-muted-foreground" />
                                {user.department}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getRoleBadge(user.role)}
                              {user.status === 'blocked' && (
                                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                                  <Ban className="w-3 h-3 mr-1" />
                                  Bloqué
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="w-4 h-4" />
                              {formatDate(user.created_at)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {user.role === 'employee' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => openAppsDialog(user)}
                                  data-testid={`apps-user-${user.id}`}
                                  title="Configurer les applications visibles"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setEditDialogOpen(true);
                                }}
                                disabled={user.id === currentUser?.id}
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={user.status === 'blocked' 
                                  ? "text-green-600 hover:text-green-700 hover:bg-green-50" 
                                  : "text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                }
                                onClick={() => {
                                  setUserToBlock(user);
                                  setBlockDialogOpen(true);
                                }}
                                disabled={user.id === currentUser?.id}
                                data-testid={`block-user-${user.id}`}
                                title={user.status === 'blocked' ? 'Débloquer' : 'Bloquer'}
                              >
                                {user.status === 'blocked' ? (
                                  <Unlock className="w-4 h-4" />
                                ) : (
                                  <Ban className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setUserToDelete(user);
                                  setDeleteDialogOpen(true);
                                }}
                                disabled={user.id === currentUser?.id}
                                data-testid={`delete-user-${user.id}`}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Requests Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Demandes d'inscription en attente
              </CardTitle>
              <CardDescription>
                Approuvez ou refusez les demandes d'accès. Un email sera envoyé automatiquement.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">Aucune demande en attente</p>
                  <p className="text-sm">Les nouvelles demandes d'inscription apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((user) => (
                    <div 
                      key={user.id}
                      className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 border rounded-lg bg-orange-50/50 border-orange-100"
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-orange-100 text-orange-700">
                          {getInitials(user.first_name, user.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {user.department && (
                            <Badge variant="outline" className="text-xs">
                              <Building className="w-3 h-3 mr-1" />
                              {user.department}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(user.created_at)}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 sm:flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 sm:flex-initial text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() => handleActivateUser(user.id, 'reject')}
                          disabled={processingUser === user.id}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Refuser
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 sm:flex-initial bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleActivateUser(user.id, 'approve')}
                          disabled={processingUser === user.id}
                        >
                          {processingUser === user.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" />
                          ) : (
                            <Check className="w-4 h-4 mr-1" />
                          )}
                          Approuver
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le rôle</DialogTitle>
            <DialogDescription>
              Changer le rôle de {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="role">Rôle</Label>
            <Select
              value={selectedUser?.role || 'employee'}
              onValueChange={(value) => handleRoleChange(selectedUser?.id, value)}
            >
              <SelectTrigger className="mt-2" data-testid="select-new-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-amber-600" />
                    Administrateur
                  </div>
                </SelectItem>
                <SelectItem value="employee">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    Employé
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              Les administrateurs ont accès à toutes les fonctionnalités de gestion.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le compte de{' '}
              <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong> ?
              <br />
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block Confirmation Dialog */}
      <AlertDialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userToBlock?.status === 'blocked' ? 'Débloquer' : 'Bloquer'} l'utilisateur ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToBlock?.status === 'blocked' ? (
                <>
                  Êtes-vous sûr de vouloir débloquer le compte de{' '}
                  <strong>{userToBlock?.first_name} {userToBlock?.last_name}</strong> ?
                  <br />
                  L'utilisateur pourra à nouveau se connecter.
                </>
              ) : (
                <>
                  Êtes-vous sûr de vouloir bloquer le compte de{' '}
                  <strong>{userToBlock?.first_name} {userToBlock?.last_name}</strong> ?
                  <br />
                  <span className="text-orange-600">
                    L'utilisateur recevra une notification et ne pourra plus se connecter. 
                    Il devra contacter la direction.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlockUser}
              className={userToBlock?.status === 'blocked' 
                ? "bg-green-600 hover:bg-green-700" 
                : "bg-orange-600 hover:bg-orange-700"
              }
              data-testid="confirm-block-user"
            >
              {userToBlock?.status === 'blocked' ? 'Débloquer' : 'Bloquer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Apps Visibility Dialog */}
      <Dialog open={appsDialogOpen} onOpenChange={setAppsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Applications visibles
            </DialogTitle>
            <DialogDescription>
              Configurer les applications accessibles pour{' '}
              <strong>{selectedUserForApps?.first_name} {selectedUserForApps?.last_name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {['Principal', 'Organisation', 'Communication', 'Espace RH', 'Système'].map(category => {
              const categoryApps = AVAILABLE_APPS.filter(app => app.category === category);
              if (categoryApps.length === 0) return null;
              
              return (
                <div key={category} className="space-y-3">
                  <h4 className="font-medium text-sm text-slate-700 border-b pb-2">{category}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {categoryApps.map(app => (
                      <label
                        key={app.key}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                      >
                        <Checkbox
                          id={`app-${app.key}`}
                          checked={selectedApps.includes(app.key)}
                          onCheckedChange={() => handleAppToggle(app.key)}
                          data-testid={`checkbox-${app.key}`}
                        />
                        <span className="text-sm">{app.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppsDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveApps} 
              disabled={savingApps}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-visible-apps"
            >
              {savingApps ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
